<script setup lang="ts">
import Modal from "@/ui/components/modal.vue";

const isDialogVisible = defineModel<boolean>();
</script>

<template>
	<v-dialog width="800" v-model="isDialogVisible" style="overflow: auto">
		<v-card class="elevation-2">
			<div class="date-formatting-help">
				<h2 class="section-title">Using Dynamic Dates</h2>

				<p class="intro-text">
					Dynamic dates allow you to reference specific times relative to the
					current moment, without consulting a calendar. They're powerful tools
					for creating flexible date ranges.
				</p>

				<div class="instruction-card">
					<h3 class="subsection-title">
						<span class="step-number">1</span>Start with a Reference Point
					</h3>
					<div class="content-box">
						<div class="keyword">now</div>
						<p>Sets your reference point to the current date and time</p>
					</div>
				</div>

				<div class="instruction-card">
					<h3 class="subsection-title">
						<span class="step-number">2</span>Add Time Modifiers
					</h3>
					<div class="content-box">
						<div class="modifier-grid">
							<div class="modifier-item">
								<div class="keyword">+</div>
								<div>Add time</div>
							</div>
							<div class="modifier-item">
								<div class="keyword">-</div>
								<div>Subtract time</div>
							</div>
						</div>

						<h4 class="unit-title">Time Units:</h4>
						<div class="units-grid">
							<div class="unit-item">
								<div class="keyword">y</div>
								<div>Years</div>
							</div>
							<div class="unit-item">
								<div class="keyword">M</div>
								<div>Months</div>
							</div>
							<div class="unit-item">
								<div class="keyword">w</div>
								<div>Weeks</div>
							</div>
							<div class="unit-item">
								<div class="keyword">d</div>
								<div>Days</div>
							</div>
							<div class="unit-item">
								<div class="keyword">h</div>
								<div>Hours</div>
							</div>
							<div class="unit-item">
								<div class="keyword">m</div>
								<div>Minutes</div>
							</div>
							<div class="unit-item">
								<div class="keyword">s</div>
								<div>Seconds</div>
							</div>
						</div>
					</div>
				</div>

				<div class="instruction-card">
					<h3 class="subsection-title">
						<span class="step-number">3</span>Apply Rounding
					</h3>
					<div class="content-box">
						<div class="keyword">/</div>
						<p>Rounds the time based on context:</p>
						<div class="rounding-options">
							<div class="rounding-item">
								<div class="field-tag from">FROM</div>
								<div>
									Rounds <strong>down</strong> to the beginning of the specified
									time unit
								</div>
							</div>
							<div class="rounding-item">
								<div class="field-tag to">TO</div>
								<div>
									Rounds <strong>up</strong> to the end of the specified time
									unit
								</div>
							</div>
						</div>
					</div>
				</div>

				<section class="examples-section">
					<h3 class="subsection-title">
						<i class="fa-solid fa-lightbulb mr-2"></i>Examples
					</h3>
					<ul class="example-cards">
						<li class="example-card">
							<div class="example-header">Start of Yesterday</div>
							<div class="example-code">now-1d/d</div>
							<div class="example-field">in the FROM field</div>
							<div class="example-desc">Gets the beginning of yesterday</div>
						</li>

						<li class="example-card">
							<div class="example-header">End of Yesterday</div>
							<div class="example-code">now-1d/d</div>
							<div class="example-field">in the TO field</div>
							<div class="example-desc">Gets the end of yesterday</div>
						</li>

						<li class="example-card">
							<div class="example-header">Last 7 Days</div>
							<div class="example-code">now-7d</div>
							<div class="example-field">in the FROM field</div>
							<div class="example-desc">Gets exactly 7 days ago from now</div>
						</li>
					</ul>
				</section>

				<div class="conclusion-box">
					<i class="fa-solid fa-magic mr-2"></i>
					With these simple rules, you can quickly specify any time period
					relative to the present moment!
				</div>
			</div>
		</v-card>
	</v-dialog>
</template>

<style lang="scss" scoped>
.date-formatting-help {
	padding: 4px 12px;
	color: #2c3e50;

	.section-title {
		font-size: 1.3rem;
		font-weight: 600;
		color: #2c3e50;
		margin: 0.3rem 0 0.6rem;
		border-bottom: 1px solid #e8eaed;
		padding-bottom: 0.3rem;
	}

	.intro-text {
		font-size: 0.9rem;
		line-height: 1.4;
		margin-bottom: 0.8rem;
		color: #5f6368;
	}

	.instruction-card {
		margin-bottom: 0.8rem;

		.subsection-title {
			font-size: 1rem;
			font-weight: 500;
			color: #1a73e8;
			margin-bottom: 0.4rem;
			display: flex;
			align-items: center;

			.step-number {
				background: #1a73e8;
				color: white;
				border-radius: 50%;
				width: 18px;
				height: 18px;
				display: flex;
				align-items: center;
				justify-content: center;
				font-size: 0.8rem;
				margin-right: 6px;
			}
		}

		.content-box {
			background: rgba(var(--color-background), 0.6);
			border-radius: 6px;
			padding: 8px 10px;
			box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);

			.keyword {
				display: inline-block;
				background: #e8f0fe;
				color: #1967d2;
				font-family: "Courier New", monospace;
				font-weight: bold;
				padding: 1px 6px;
				border-radius: 3px;
				margin-bottom: 4px;
				border: 1px solid #d2e3fc;
			}

			p {
				margin-bottom: 6px;
				line-height: 1.4;
				font-size: 0.9rem;
			}

			.modifier-grid {
				display: flex;
				gap: 12px;
				margin-bottom: 8px;

				.modifier-item {
					display: flex;
					flex-direction: column;
					align-items: center;
					font-size: 0.9rem;
				}
			}

			.unit-title {
				font-size: 0.9rem;
				font-weight: 500;
				margin: 6px 0 3px;
			}

			.units-grid {
				display: grid;
				grid-template-columns: repeat(auto-fill, minmax(60px, 1fr));
				gap: 8px;

				.unit-item {
					display: flex;
					flex-direction: column;
					align-items: center;
					font-size: 0.85rem;

					.keyword {
						margin-bottom: 2px;
						min-width: 24px;
						text-align: center;
					}
				}
			}

			.rounding-options {
				margin-top: 6px;

				.rounding-item {
					display: flex;
					align-items: center;
					margin-bottom: 4px;
					font-size: 0.85rem;

					.field-tag {
						font-size: 0.65rem;
						font-weight: bold;
						padding: 1px 6px;
						border-radius: 3px;
						margin-right: 6px;

						&.from {
							background-color: #e6f4ea;
							color: #137333;
						}

						&.to {
							background-color: #fce8e6;
							color: #c5221f;
						}
					}
				}
			}
		}
	}

	.examples-section {
		margin-top: 1rem;

		.subsection-title {
			font-size: 1rem;
			font-weight: 500;
			color: #1a73e8;
			margin-bottom: 0.5rem;
		}

		.example-cards {
			display: grid;
			grid-template-columns: 1fr 1fr 1fr;
			border-bottom: solid 1px #e8eaed;
			padding-bottom: 1rem;
			width: 100%;
			list-style: none;
			gap: 10px;
			padding-left: 0;

			.example-card {
				background: rgba(var(--color-background), 0.6);
				border-radius: 6px;
				padding: 10px;
				box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
				border-left: 3px solid #1a73e8;

				.example-header {
					font-weight: 500;
					margin-bottom: 4px;
					color: #202124;
					font-size: 0.9rem;
				}

				.example-code {
					font-family: "Courier New", monospace;
					background: #e8f0fe;
					color: #1967d2;
					padding: 2px 6px;
					border-radius: 3px;
					display: inline-block;
					margin-bottom: 4px;
					font-weight: bold;
					border: 1px solid #d2e3fc;
					font-size: 0.85rem;
				}

				.example-field {
					font-size: 0.7rem;
					color: #5f6368;
					margin-bottom: 4px;
				}

				.example-desc {
					font-size: 0.8rem;
					color: #5f6368;
				}
			}
		}
	}

	.conclusion-box {
		background: #e8f0fe;
		border-radius: 6px;
		padding: 8px;
		margin-top: 0.8rem;
		margin-bottom: 0.5rem;
		color: #1967d2;
		font-weight: 500;
		text-align: center;
		font-size: 0.9rem;
		box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
		border: 1px solid #d2e3fc;
	}
}
</style>
